import React, { useState, useEffect } from 'react'
import {
  Mail,
  Lock,
  Loader2,
  AlertCircle,
  Send,
  ArrowLeft,
  CheckCircle2,
  User,
  KeyRound,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { XlevenLogo } from '../Common/XlevenLogo'
import { AppFooter } from '../Common/AppFooter'
import SEO from '../Common/SEO'
import {
  resendVerificationEmail,
  resetPasswordForEmail,
} from '../../services/authService'
import { t } from '../../i18n'

interface LoginViewProps {
  onBack: () => void
  onGoToSignUp: () => void
  onLoginSuccess: () => void
}

type ViewMode = 'login' | 'forgot-password' | 'reset-success'

export const LoginView: React.FC<LoginViewProps> = ({
  onBack,
  onGoToSignUp,
  onLoginSuccess,
}) => {
  const { login, loginWithGoogle, loginWithKakao, loginWithNaver, isLoading } =
    useAuth()
  const { lang } = useLanguage()

  const [mode, setViewMode] = useState<ViewMode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  const [isResending, setIsResending] = useState(false)
  const [resendStatus, setResendStatus] = useState<'idle' | 'sent'>('idle')
  const [isResetting, setIsResetting] = useState(false)

  // 입력값이 변경되면 오류 메시지를 초기화합니다.
  useEffect(() => {
    if (error) setError('')
  }, [email, password])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setResendStatus('idle')

    if (!email || !password) {
      setError(t(lang, 'fillAllFields'))
      return
    }

    try {
      await login(email, password)
      const redirectTo = sessionStorage.getItem('redirectAfterLogin')
      if (redirectTo) {
        sessionStorage.removeItem('redirectAfterLogin')
        if (window.navigateTo) {
          window.navigateTo(redirectTo.replace(/^#/, ''))
          return
        }
      }
      onLoginSuccess()
    } catch (err: any) {
      console.warn('Auth attempt failed:', err.message)
      const msg = err.message || ''
      if (msg.includes('Invalid login credentials')) {
        setError(t(lang, 'invalidCredentials'))
      } else if (msg.includes('Email not confirmed')) {
        setError('EmailNotConfirmed')
      } else if (msg.includes('rate limit')) {
        setError(t(lang, 'tooManyRequests'))
      } else {
        setError(msg)
      }
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!email) {
      setError(t(lang, 'emailRequired'))
      return
    }

    setIsResetting(true)
    try {
      await resetPasswordForEmail(email)
      setViewMode('reset-success')
    } catch (err: any) {
      setError(t(lang, 'resetFailed') + ': ' + err.message)
    } finally {
      setIsResetting(false)
    }
  }

  const handleResendEmail = async () => {
    if (!email) return
    setIsResending(true)
    try {
      await resendVerificationEmail(email)
      setResendStatus('sent')
    } catch (err: any) {
      setError(t(lang, 'resendFailed') + ': ' + err.message)
    } finally {
      setIsResending(false)
    }
  }

  if (mode === 'reset-success') {
    return (
      <div className='min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95 duration-500'>
        <div className='w-24 h-24 bg-emerald-50 rounded-[40px] flex items-center justify-center text-emerald-500 mb-8 border border-emerald-100 shadow-xl shadow-emerald-50'>
          <CheckCircle2
            size={48}
            strokeWidth={2.5}
          />
        </div>
        <h1 className='text-3xl font-black text-slate-900 mb-3 tracking-tighter uppercase'>
          {t(lang, 'checkMail')}
        </h1>
        <p className='text-slate-400 text-sm font-medium leading-relaxed max-w-xs mb-10'>
          {t(lang, 'resetInstructionsSent')}{' '}
          <span className='text-slate-900 font-bold'>{email}</span>.
        </p>
        <button
          onClick={() => setViewMode('login')}
          className='w-full max-w-[320px] bg-slate-900 text-white py-5 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] md:hover:bg-rose-500 transition-all active:scale-95 shadow-xl shadow-slate-100'
        >
          {t(lang, 'returnToPortal')}
        </button>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-white flex flex-col p-0 relative'>
      <SEO title={mode === 'forgot-password' ? t(lang, 'resetIntel') : t(lang, 'signInAccount')} />
      <div className='flex-1 flex flex-col items-center justify-center p-6 py-12'>
        <div className='w-full max-w-[420px] animate-in fade-in slide-in-from-bottom-8 duration-700'>
          {/* Header */}
          <div className='flex flex-col items-center mb-10'>
            <div className='mb-8 scale-110 md:hidden'>
              <XlevenLogo />
            </div>
            <h1 className='text-3xl font-black text-slate-900 tracking-tighter mb-2 uppercase text-center'>
              {mode === 'forgot-password'
                ? t(lang, 'resetIntel')
                : t(lang, 'welcome')}
            </h1>
            <p className='text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] text-center'>
              {mode === 'forgot-password'
                ? t(lang, 'verifyIdentity')
                : t(lang, 'enterCredentials')}
            </p>
          </div>

          {mode === 'login' ? (
            <div className='space-y-8'>
              <div className='grid grid-cols-1 gap-3'>
                {/* Google Login */}
                <button
                  onClick={loginWithGoogle}
                  disabled={isLoading}
                  className='w-full flex items-center justify-center gap-3 bg-white border border-gray-200 py-3.5 rounded-2xl md:hover:border-gray-400 md:hover:bg-gray-50 transition-all active:scale-[0.98] group disabled:opacity-50'
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                  >
                    <path
                      d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                      fill='#4285F4'
                    />
                    <path
                      d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                      fill='#34A853'
                    />
                    <path
                      d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z'
                      fill='#FBBC05'
                    />
                    <path
                      d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                      fill='#EA4335'
                    />
                  </svg>
                  <span className='text-xs font-black text-slate-700 uppercase tracking-widest'>
                    {t(lang, 'continueWithGoogle')}
                  </span>
                </button>

                {/* Kakao Login */}
                <button
                  onClick={loginWithKakao}
                  disabled={isLoading}
                  className='w-full flex items-center justify-center gap-3 bg-[#FEE500] py-3.5 rounded-2xl md:hover:brightness-105 transition-all active:scale-[0.98] group disabled:opacity-50'
                >
                  <svg
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='black'
                  >
                    <path d='M12 3c-4.97 0-9 3.185-9 7.115 0 2.558 1.707 4.8 4.315 6.091l-1.091 4.011c-.046.171.144.316.29.215l4.785-3.175c.548.064 1.114.098 1.701.098 4.97 0 9-3.185 9-7.115S16.97 3 12 3z' />
                  </svg>
                  <span className='text-xs font-black text-black uppercase tracking-widest'>
                    {t(lang, 'continueWithKakao')}
                  </span>
                </button>

                {/* Naver Login */}
                {/* <button 
                                    onClick={loginWithNaver}
                                    disabled={isLoading}
                                    className="w-full flex items-center justify-center gap-3 bg-[#03C75A] py-3.5 rounded-2xl md:hover:brightness-105 transition-all active:scale-[0.98] group disabled:opacity-50"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                        <path d="M16.273 12.845L7.376 0H0v24h7.726V11.155L16.624 24H24V0h-7.727v12.845z"/>
                                    </svg>
                                    <span className="text-xs font-black text-white uppercase tracking-widest">{t(lang, 'continueWithNaver')}</span>
                                </button> */}
              </div>

              <div className='flex items-center gap-4'>
                <div className='flex-1 h-px bg-gray-100'></div>
                <span className='text-[9px] font-black text-slate-300 uppercase tracking-[0.3em]'>
                  {t(lang, 'or')}
                </span>
                <div className='flex-1 h-px bg-gray-100'></div>
              </div>

              <form
                onSubmit={handleSubmit}
                className='space-y-5'
              >
                <div className='space-y-4'>
                  <div className='space-y-1.5'>
                    <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                      {t(lang, 'emailAddress')}
                    </label>
                    <div className='relative group'>
                      <Mail
                        className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                        size={18}
                      />
                      <input
                        type='email'
                        placeholder='name@example.com'
                        className='w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className='space-y-1.5'>
                    <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                      {t(lang, 'password')}
                    </label>
                    <div className='relative group'>
                      <KeyRound
                        className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                        size={18}
                      />
                      <input
                        type='password'
                        placeholder='••••••••'
                        className='w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200'
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <div className='bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-center gap-3 animate-in shake duration-300'>
                    <AlertCircle
                      size={16}
                      className='text-rose-500 shrink-0'
                    />
                    <p className='text-rose-600 text-[11px] font-black leading-tight uppercase'>
                      {error}
                    </p>
                  </div>
                )}

                <div className='flex justify-end items-center px-1'>
                  <button
                    type='button'
                    onClick={() => setViewMode('forgot-password')}
                    className='text-[10px] font-black uppercase tracking-widest text-slate-400 md:hover:text-rose-500 transition-colors'
                  >
                    {t(lang, 'forgotPassword')}
                  </button>
                </div>

                <button
                  type='submit'
                  disabled={isLoading}
                  className='w-full bg-slate-900 text-white py-5 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] md:hover:bg-rose-500 transition-all shadow-xl shadow-slate-100 disabled:opacity-70 flex items-center justify-center gap-3 active:scale-[0.98] mt-4'
                >
                  {isLoading ? (
                    <Loader2
                      className='animate-spin'
                      size={20}
                    />
                  ) : (
                    t(lang, 'signInAccount')
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className='space-y-8 animate-in slide-in-from-right-4 duration-500'>
              <form
                onSubmit={handleResetPassword}
                className='space-y-6'
              >
                <div className='space-y-2'>
                  <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                    {t(lang, 'accountEmail')}
                  </label>
                  <div className='relative group'>
                    <Mail
                      className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                      size={20}
                    />
                    <input
                      type='email'
                      placeholder={t(lang, 'enterRegisteredEmail')}
                      className='w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <div className='bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-center gap-3 animate-in shake duration-300'>
                    <AlertCircle
                      size={16}
                      className='text-rose-500 shrink-0'
                    />
                    <p className='text-rose-600 text-[11px] font-black leading-tight uppercase'>
                      {error}
                    </p>
                  </div>
                )}

                <button
                  type='submit'
                  disabled={isResetting || !email}
                  className='w-full bg-slate-900 text-white py-5 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] md:hover:bg-rose-500 transition-all shadow-xl shadow-slate-100 disabled:opacity-70 flex items-center justify-center gap-3 active:scale-[0.98]'
                >
                  {isResetting ? (
                    <Loader2
                      className='animate-spin'
                      size={20}
                    />
                  ) : (
                    t(lang, 'authorizeReset')
                  )}
                </button>

                <button
                  type='button'
                  onClick={() => setViewMode('login')}
                  className='w-full text-slate-400 font-black text-[10px] uppercase tracking-[0.2em] md:hover:text-slate-900 transition-colors flex items-center justify-center gap-2'
                >
                  <ArrowLeft size={14} /> {t(lang, 'backToEntry')}
                </button>
              </form>
            </div>
          )}

          <div className='mt-16 text-center border-t border-slate-50 pt-10'>
            <p className='text-slate-400 text-[13px] font-medium'>
              {t(lang, 'noAccount')}{' '}
              <button
                onClick={onGoToSignUp}
                className='text-rose-500 font-black md:hover:underline underline-offset-4 decoration-2 uppercase tracking-tight ml-1'
              >
                {t(lang, 'createAccount')}
              </button>
            </p>
          </div>
        </div>
      </div>
      <AppFooter lang={lang} />
    </div>
  )
}
